import React, { useEffect, useRef, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { drawTwinShareCard, downloadTwinShareCard, CARD_THEMES } from "@/lib/twinShareCard";
import { track } from "@/lib/analytics";

const LABELS = { kicker: "BORN ON THIS DAY", sub: "FAMOUS BIRTHDAYS", row: () => "Born on this day", empty: "No famous births on record yet.", cta: "Who shares your birthday? oddlytrue.co/birthdays" };

/** "Born on this day" story card (1080x1920) for the /birthdays page: preview, theme picker, PNG download, WhatsApp / X share. */
export const BornTodayShareCard = ({ ddmm, dateLabel, people }) => {
  const [theme, setTheme] = useState("ink");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const twins = people.slice(0, 3);
  const card = { name: "", dateLabel, signName: "", glyph: "", twins, theme, labels: LABELS };

  useEffect(() => { if (ref.current) drawTwinShareCard(ref.current, card); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, ddmm, people.length]);

  const names = twins.map((t) => t.name);
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "some remarkable people";
  const url = `${window.location.origin}/birthdays?date=${ddmm}`;
  const text = `Born on ${dateLabel}: ${list}. Who shares your birthday?`;
  const download = async () => {
    setBusy(true);
    try { await downloadTwinShareCard(card); track("born_today_card_download", { date: ddmm, category: "share", theme }); toast.success("Card downloaded — post it to your story"); }
    catch { toast.error("Could not build the card"); }
    finally { setBusy(false); }
  };
  const btn = "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full border border-[#e5e7eb] bg-white text-ink text-[13px] font-semibold hover:border-gold hover:text-gold transition-colors";
  return (
    <div data-testid="born-today-share" className="mt-6 flex flex-col sm:flex-row gap-4 items-stretch bg-white border border-[#e5e7eb] rounded-2xl p-4">
      <canvas ref={ref} data-testid="born-today-card-preview" aria-label="Share card preview" className="w-[108px] h-[192px] rounded-lg border border-[#e5e7eb] shrink-0 bg-paper mx-auto sm:mx-0" />
      <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold inline-flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" /> Share this day</p>
          <p className="mt-1 text-[14px] text-ink">A story card for {dateLabel} with {twins.length ? `${twins.length} famous birthday${twins.length === 1 ? "" : "s"}` : "today's date"} — ready for Instagram, WhatsApp or X.</p>
          <div className="mt-2 flex gap-2" role="radiogroup" aria-label="Card style">
            {Object.values(CARD_THEMES).map((t) => (
              <button key={t.id} type="button" role="radio" aria-checked={theme === t.id} data-testid={`born-today-style-${t.id}`} onClick={() => setTheme(t.id)}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[12px] font-semibold transition-colors ${theme === t.id ? "border-gold bg-paper text-ink" : "border-[#e5e7eb] bg-white text-ink-muted hover:border-gold"}`}>
                <span className="w-3 h-3 rounded-full border border-[#e5e7eb]" style={{ background: t.bg }} />{t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={download} disabled={busy} data-testid="born-today-download" className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full bg-ink text-white text-[13px] font-semibold hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
            <Download className="w-4 h-4" /> {busy ? "Building…" : "Download PNG"}
          </button>
          <a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`} target="_blank" rel="noopener noreferrer" data-testid="born-today-whatsapp" className={btn}>WhatsApp</a>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid="born-today-x" className={btn}>Share to X</a>
        </div>
      </div>
    </div>
  );
};
